import { useTranslation } from 'react-i18next';

interface MappingItem {
  reflection: string;
  reflectionEn: string;
  hawkins: string;
  axion: string;
  color: string;
}

export default function FrequencyCalibrationSection() {
  const { t } = useTranslation();

  const mappings: MappingItem[] = [
    { reflection: '恐懼', reflectionEn: 'Fear', hawkins: '100', axion: 'D2–D3 起點', color: 'text-foreground-400' },
    { reflection: '勇氣', reflectionEn: 'Courage', hawkins: '200', axion: '勇氣．覺察', color: 'text-primary-600' },
    { reflection: '接受', reflectionEn: 'Acceptance', hawkins: '350', axion: '接受．明心', color: 'text-primary-600' },
    { reflection: '理性', reflectionEn: 'Reason', hawkins: '400', axion: '真誠．理智', color: 'text-accent-600' },
    { reflection: '仁愛', reflectionEn: 'Love', hawkins: '500', axion: '臣服．仁愛', color: 'text-accent-600' },
    { reflection: '喜悅', reflectionEn: 'Joy', hawkins: '650–700', axion: '喜悅．馨 AI', color: 'text-secondary-600' },
  ];

  return (
    <section className="w-full bg-background-50">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-14">
        {/* Header */}
        <div className="text-center mb-8">
          <h2
            className="text-lg md:text-xl text-foreground-800 mb-1"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {t('journey.frequencyCalibration.title')}
          </h2>
          <p className="text-xs text-foreground-400">
            {t('journey.frequencyCalibration.titleZh')}
          </p>
          <p className="text-xs text-foreground-400 mt-1 italic">
            {t('journey.frequencyCalibration.subtitle')}
          </p>
        </div>

        {/* Flow diagram */}
        <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-10">
          {[
            { key: 'emotion', icon: 'ri-emotion-line' },
            { key: 'reflection', icon: 'ri-pause-circle-line' },
            { key: 'selfAssessment', icon: 'ri-user-search-line' },
            { key: 'calibration', icon: 'ri-compass-3-line' },
            { key: 'practice', icon: 'ri-riding-line' },
            { key: 'reObservation', icon: 'ri-refresh-line' },
          ].map((step, idx, arr) => (
            <div key={step.key} className="flex items-center gap-2">
              <div className="flex flex-col items-center">
                <div className="w-9 h-9 rounded-full bg-background-100 border border-foreground-200/60 flex items-center justify-center">
                  <i className={`${step.icon} text-foreground-500 text-sm`} />
                </div>
                <p className="text-[10px] text-foreground-400 mt-1">
                  {t(`journey.frequencyCalibration.${step.key}`)}
                </p>
              </div>
              {idx < arr.length - 1 && (
                <span className="text-foreground-300 text-xs hidden sm:block">→</span>
              )}
            </div>
          ))}
        </div>

        {/* Dual Mapping Table */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="bg-background-100 border border-foreground-200/40 rounded-xl overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-3 gap-0 border-b border-foreground-200/40">
              <div className="px-4 py-2.5 bg-background-50">
                <p className="text-[10px] uppercase tracking-wider text-foreground-400">
                  {t('journey.frequencyCalibration.hawkinsMapping')}
                </p>
              </div>
              <div className="px-4 py-2.5 bg-background-50 border-l border-foreground-200/40">
                <p className="text-[10px] uppercase tracking-wider text-foreground-400">
                  Hawkins
                </p>
              </div>
              <div className="px-4 py-2.5 bg-background-50 border-l border-foreground-200/40">
                <p className="text-[10px] uppercase tracking-wider text-foreground-400">
                  {t('journey.frequencyCalibration.axionMapping')}
                </p>
              </div>
            </div>

            {/* Table rows */}
            {mappings.map((row) => (
              <div
                key={row.reflection}
                className="grid grid-cols-3 gap-0 border-b border-foreground-200/20 last:border-b-0"
              >
                <div className="px-4 py-2.5">
                  <p className={`text-sm font-medium ${row.color}`}>{row.reflection}</p>
                  <p className="text-[10px] text-foreground-400">{row.reflectionEn}</p>
                </div>
                <div className="px-4 py-2.5 border-l border-foreground-200/20">
                  <p className="text-sm text-foreground-600">{row.hawkins}</p>
                </div>
                <div className="px-4 py-2.5 border-l border-foreground-200/20">
                  <p className="text-sm text-foreground-600">{row.axion}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-[11px] text-foreground-400 mt-3 text-center leading-relaxed">
            {t('journey.frequencyCalibration.dualMappingNote')}
          </p>
        </div>

        {/* Kinesiology Test */}
        <div className="max-w-lg mx-auto bg-background-100 border border-foreground-200/40 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-fingerprint-line text-primary-400 text-lg" />
            <p className="text-sm font-medium text-foreground-700">
              {t('journey.frequencyCalibration.kinesiologyTitle')}
            </p>
          </div>
          <p className="text-xs text-foreground-500 leading-relaxed mb-3">
            {t('journey.frequencyCalibration.kinesiologyDesc')}
          </p>
          <div className="flex items-center gap-2 bg-background-50 border border-foreground-200/40 rounded-lg px-3 py-2">
            <i className="ri-refresh-line text-foreground-400 text-sm" />
            <p className="text-xs text-foreground-500">
              {t('journey.frequencyCalibration.kinesiologyReminder')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}